import type { ReactNode } from 'react'
import React, { createContext, useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'

import type { TableHeaders, TableProps } from '@fourthwall/components'
import {
  ConfirmModal,
  Dropdown,
  NumberString,
  Tag,
  ToolbarButton,
  useMediaQueryContext,
  useModalContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { useFormik } from '@fourthwall/utils/dist/formik'
import { useTiersQuery } from '@modules/Dashboard/Memberships/modules/Perks/hooks/useTiersQuery'
import { useAccountSettingsQuery } from '@modules/Dashboard/Memberships/modules/Settings/hooks/useAccountSettingsQuery'
import { reverse, routing } from '@utils/routing'

import { BadgePreview } from '../../components/BadgePreview'
import { BadgeTableCell } from '../../components/BadgeTableCell'
import { useBadgesQuery } from '../../hooks/useBadgesQuery'
import { useDeleteCustomBadgeMutation } from '../../hooks/useDeleteCustomBadgeMutation'
import { useDisableCustomBadgeMutation } from '../../hooks/useDisableCustomBadgeMutation'
import { useDisableMemberStreakBadgeMutation } from '../../hooks/useDisableMemberStreakBadgeMutation'
import { useDisableVerifiedBadgeMutation } from '../../hooks/useDisableVerifiedBadgeMutation'
import { useEnableCustomBadgeMutation } from '../../hooks/useEnableCustomBadgeMutation'
import { useEnableMemberStreakBadgeMutation } from '../../hooks/useEnableMemberStreakBadgeMutation'
import { useEnableVerifiedBadgeMutation } from '../../hooks/useEnableVerifiedBadgeMutation'
import { useUpdateAccountSettingsBadges } from '../../hooks/useUpdateAccountSettingsBadgesMutation'
import { getEmojiIcon } from '../../utils'
import { validationSchema } from './consts'
import type { Badge as BadgeType, FormValues, TableData } from './types'
import { getRules } from './utills'

type Props = { children: ReactNode }

type BadgesContextType = {
  badges?: BadgeType[]
  table: TableProps
  form: ReturnType<typeof useFormik<FormValues>>
  isFetching: boolean
  isLoading: boolean
}

export const BadgesContext = createContext<BadgesContextType | undefined>(undefined)

export const BadgesProvider = ({ children }: Props) => {
  const modal = useModalContext()
  const media = useMediaQueryContext()

  const { badgesQuery } = useBadgesQuery()
  const { accountSettingsQuery } = useAccountSettingsQuery()
  const { tiersQuery } = useTiersQuery()

  const { enableCustomBadgeMutation } = useEnableCustomBadgeMutation()
  const { disableCustomBadgeMutation } = useDisableCustomBadgeMutation()
  const { deleteCustomBadgeMutation } = useDeleteCustomBadgeMutation()
  const { enableVerifiedBadgeMutation } = useEnableVerifiedBadgeMutation()
  const { disableVerifiedBadgeMutation } = useDisableVerifiedBadgeMutation()
  const { enableMemberStreakBadgeMutation } = useEnableMemberStreakBadgeMutation()
  const { disableMemberStreakBadgeMutation } = useDisableMemberStreakBadgeMutation()

  const { updateAccountSettingsBadgesMutation } = useUpdateAccountSettingsBadges()

  const enableBadge = useCallback(
    (id: number, type: BadgeType['type']) => {
      modal.open(ConfirmModal, {
        title: 'Are you sure you want to enable this badge?',
        confirmLabel: 'Enable',
        onConfirm: () => {
          if (type === 'MemberStreakDefault') {
            enableMemberStreakBadgeMutation.mutate([], {
              onSuccess: badgesQuery.refetch,
            })
          } else if (type === 'Custom') {
            enableCustomBadgeMutation.mutate([{ id }], {
              onSuccess: badgesQuery.refetch,
            })
          } else if (type === 'Verified') {
            enableVerifiedBadgeMutation.mutate([], {
              onSuccess: badgesQuery.refetch,
            })
          }
        },
      })
    },
    [
      badgesQuery,
      enableCustomBadgeMutation,
      enableMemberStreakBadgeMutation,
      enableVerifiedBadgeMutation,
      modal,
    ],
  )

  const disableBadge = useCallback(
    (id: number, type: BadgeType['type']) => {
      modal.open(ConfirmModal, {
        title: 'Are you sure you want to disable this badge?',
        confirmLabel: 'Disable',
        onConfirm: () => {
          if (type === 'MemberStreakDefault') {
            disableMemberStreakBadgeMutation.mutate([], {
              onSuccess: badgesQuery.refetch,
            })
          } else if (type === 'Custom') {
            disableCustomBadgeMutation.mutate([{ id }], {
              onSuccess: badgesQuery.refetch,
            })
          } else if (type === 'Verified') {
            disableVerifiedBadgeMutation.mutate([], {
              onSuccess: badgesQuery.refetch,
            })
          }
        },
      })
    },
    [
      badgesQuery,
      disableCustomBadgeMutation,
      disableMemberStreakBadgeMutation,
      disableVerifiedBadgeMutation,
      modal,
    ],
  )

  const deleteBadge = useCallback(
    (badgeId: number) => {
      modal.open(ConfirmModal, {
        title: 'Are you sure you want to delete this badge?',
        confirmLabel: 'Delete',
        onConfirm: () => {
          deleteCustomBadgeMutation.mutate([{ id: badgeId }], {
            onSuccess: badgesQuery.refetch,
          })
        },
      })
    },
    [badgesQuery, deleteCustomBadgeMutation, modal],
  )

  const getAdditionalBadgeText = (additionalBadge: BadgeType) => {
    const memberStreakRule = additionalBadge.rules.find(
      (rule) =>
        rule.type === 'MemberStreak' &&
        rule.monthsFrom === undefined &&
        rule.monthsTo &&
        rule.monthsTo === 1,
    )

    if (!memberStreakRule) return additionalBadge.text

    return 'New member'
  }

  const tableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'badge',
        label: '',
        render: (badge) => {
          const badges: React.JSX.Element[] = []

          const hasCustomNewMemberBadge = !!badge.additionalBadges?.find((additionalBadge) =>
            additionalBadge.rules.find((rule) => {
              return (
                rule.type === 'MemberStreak' &&
                rule.monthsFrom === undefined &&
                (rule.monthsTo === undefined || rule.monthsTo >= 1)
              )
            }),
          )

          if (!hasCustomNewMemberBadge && badge.type === 'MemberStreakDefault') {
            badges.push(
              <BadgePreview
                emoji={getEmojiIcon(badge.icon)}
                text="New member"
                borderColor={badge.borderColor}
                backgroundColor={badge.backgroundColor}
                textColor={badge.textColor}
                iconOnly={badge.minimal}
              />,
            )
          }

          badges.push(
            <BadgePreview
              emoji={getEmojiIcon(badge.icon)}
              text={badge.text}
              borderColor={badge.borderColor}
              backgroundColor={badge.backgroundColor}
              textColor={badge.textColor}
              verified={badge.type === 'Verified'}
              iconOnly={badge.minimal}
            />,
          )

          badge.additionalBadges?.forEach((additionalBadge) => {
            const renderNewMemberBadge = !!additionalBadge.rules.find((rule) => {
              return (
                rule.type === 'MemberStreak' &&
                rule.monthsFrom === undefined &&
                (rule.monthsTo === undefined || rule.monthsTo >= 2)
              )
            })

            if (renderNewMemberBadge) {
              badges.push(
                <BadgePreview
                  key={additionalBadge.id}
                  emoji={getEmojiIcon(additionalBadge.icon)}
                  text="New member"
                  borderColor={additionalBadge.borderColor}
                  backgroundColor={additionalBadge.backgroundColor}
                  textColor={additionalBadge.textColor}
                  iconOnly={additionalBadge.minimal}
                />,
              )
            }

            badges.push(
              <BadgePreview
                key={additionalBadge.id}
                emoji={getEmojiIcon(additionalBadge.icon)}
                text={getAdditionalBadgeText(additionalBadge)}
                borderColor={additionalBadge.borderColor}
                backgroundColor={additionalBadge.backgroundColor}
                textColor={additionalBadge.textColor}
                iconOnly={additionalBadge.minimal}
              />,
            )
          })

          return (
            <BadgeTableCell
              rules={getRules(badge, tiersQuery.data)}
              hiddenBadgesCount={badges.length > 3 ? badges.length - 3 : 0}
            >
              {badges.slice(0, 3)}
            </BadgeTableCell>
          )
        },
        width: 5,
      },
      {
        id: 'status',
        label: '',
        render: (badge) => (
          <Tag
            label={badge.active ? 'Live' : 'Disabled'}
            appearance={badge.active ? 'success' : 'alert'}
          />
        ),
        width: 2,
      },
      {
        id: 'earned',
        label: '',
        render: (badge) => {
          if (
            badge.type === 'Verified' &&
            badge.creatorApplicationSettings?.type === 'ApplyToAll'
          ) {
            return 'Applied to team'
          }

          const additionalBadgesSupportersCount = badge.additionalBadges
            ? badge.additionalBadges?.reduce(
                (total, additionalBadge) => total + additionalBadge.supportersCount,
                0,
              )
            : 0

          const additionalBadgesManuallyAssignedSupportersCount = badge.additionalBadges
            ? badge.additionalBadges?.reduce(
                (total, additionalBadge) =>
                  total +
                  (additionalBadge.manuallyAssignedSupporterIds
                    ? additionalBadge.manuallyAssignedSupporterIds.length
                    : 0),
                0,
              )
            : 0

          const additionalBadgesEarned =
            additionalBadgesSupportersCount - additionalBadgesManuallyAssignedSupportersCount

          const badgeManuallyAssignedSupportersCount =
            badge.manuallyAssignedSupporterIds?.length || 0

          const badgeManuallyAssignedTeamStaffCount =
            badge.creatorApplicationSettings?.type === 'ApplyToSelectedEmails'
              ? badge.creatorApplicationSettings.emails.length
              : 0

          const badgeEarned = badge.supportersCount - badgeManuallyAssignedSupportersCount

          const totalEarned = additionalBadgesEarned + badgeEarned
          const totalGiven =
            additionalBadgesManuallyAssignedSupportersCount +
            badgeManuallyAssignedSupportersCount +
            badgeManuallyAssignedTeamStaffCount

          return (
            <div style={{ display: 'grid', gap: 6 }}>
              {(totalEarned > 0 || totalGiven === 0) && (
                <span>
                  <NumberString value={totalEarned} /> earned
                </span>
              )}
              {totalGiven > 0 && (
                <span>
                  <NumberString value={totalGiven} /> given
                </span>
              )}
            </div>
          )
        },
        width: 2,
      },
      {
        id: 'settings',
        label: '',
        render: (badge) => {
          return (
            <Dropdown
              content={
                <>
                  <Dropdown.Item
                    label={badge.active ? 'Disable' : 'Enable'}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()

                      badge.active
                        ? disableBadge(badge.id, badge.type)
                        : enableBadge(badge.id, badge.type)
                    }}
                  />
                  {badge.type === 'Custom' && (
                    <Dropdown.Item
                      label="Delete"
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()

                        deleteBadge(badge.id)
                      }}
                    />
                  )}
                </>
              }
              position="bottom-end"
            >
              <ToolbarButton icon={DotsHorizontalIcon} />
            </Dropdown>
          )
        },
        width: 1,
        justifyContent: 'end',
      },
    ],
    [tiersQuery.data, deleteBadge, disableBadge, enableBadge],
  )

  const mobileTableHeaders: TableHeaders<TableData> = useMemo(
    () => [
      {
        id: 'badge',
        label: '',
        render: (badge) => {
          return (
            <BadgeTableCell rules={getRules(badge, tiersQuery.data)}>
              <BadgePreview
                key={badge.text}
                emoji={getEmojiIcon(badge.icon)}
                text={badge.text}
                borderColor={badge.borderColor}
                backgroundColor={badge.backgroundColor}
                textColor={badge.textColor}
                iconOnly={badge.minimal}
              />
              {badge.additionalBadges?.map((additionalBadge) => (
                <BadgePreview
                  key={additionalBadge.id}
                  emoji={getEmojiIcon(additionalBadge.icon)}
                  text={additionalBadge.text}
                  borderColor={additionalBadge.borderColor}
                  backgroundColor={additionalBadge.backgroundColor}
                  textColor={additionalBadge.textColor}
                  iconOnly={additionalBadge.minimal}
                />
              ))}
            </BadgeTableCell>
          )
        },
        width: 3,
      },
      {
        id: 'status',
        label: '',
        render: (badge) => {
          return (
            <Tag
              label={badge.active ? 'Live' : 'Disabled'}
              appearance={badge.active ? 'success' : 'alert'}
            />
          )
        },
        width: 1,
      },
    ],
    [tiersQuery.data],
  )

  const tableData: TableData[] = useMemo(() => {
    if (!badgesQuery.data) return []

    const typesOrder = [
      'MemberStreakDefault',
      'MemberStreakCustom',
      'Verified',
      'Custom',
    ] satisfies BadgeType['type'][]

    const sortedBadges = badgesQuery.data.badges.sort((a, b) => {
      return typesOrder.indexOf(a.type) - typesOrder.indexOf(b.type)
    })

    const streakDefaultAndCustomAndVerifiedBadges = sortedBadges.filter(
      (badge) =>
        badge.type === 'MemberStreakDefault' ||
        badge.type === 'Custom' ||
        badge.type === 'Verified',
    )

    const customStreakBadges = sortedBadges.filter((badge) => badge.type === 'MemberStreakCustom')

    return streakDefaultAndCustomAndVerifiedBadges.map((badge) => {
      if (badge.type === 'MemberStreakDefault') {
        return {
          ...badge,
          additionalBadges: customStreakBadges,
        }
      }

      return badge
    })
  }, [badgesQuery.data])

  const table: TableProps<TableData> = useMemo(
    () => ({
      data: tableData,
      headers: media.tablet ? tableHeaders : mobileTableHeaders,
      emptyMessage: 'You haven’t created any badges yet.',
      minWidth: 0,
      rowComponent: Link,
      getRowLink: (badge: any) => ({
        pathname: reverse(routing.memberships.settings.badges.badge.self, {
          badgeId: badge.id,
        }),
        search: location.search,
      }),
    }),
    [tableData, media, tableHeaders, mobileTableHeaders],
  )

  const form = useFormik<FormValues>({
    initialValues: {
      uncollapsedBadgesCount:
        accountSettingsQuery.data?.badges.uncollapsedDisplayCount.toString() || '0',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values) => {
      updateAccountSettingsBadgesMutation.mutate(
        [{ uncollapsedBadges: parseInt(values.uncollapsedBadgesCount) }],
        {
          onSuccess: accountSettingsQuery.refetch,
        },
      )
    },
  })

  const value = useMemo(
    () => ({
      badges: badgesQuery.data?.badges,
      table,
      form,
      isFetching: !badgesQuery.data?.badges || !accountSettingsQuery.data || !tiersQuery.data,
      isLoading:
        badgesQuery.isFetching ||
        enableCustomBadgeMutation.isLoading ||
        disableCustomBadgeMutation.isLoading ||
        deleteCustomBadgeMutation.isLoading ||
        enableMemberStreakBadgeMutation.isLoading ||
        disableMemberStreakBadgeMutation.isLoading ||
        enableVerifiedBadgeMutation.isLoading ||
        disableVerifiedBadgeMutation.isLoading ||
        updateAccountSettingsBadgesMutation.isLoading,
    }),
    [
      badgesQuery,
      accountSettingsQuery,
      tiersQuery,
      table,
      form,
      enableCustomBadgeMutation.isLoading,
      disableCustomBadgeMutation.isLoading,
      deleteCustomBadgeMutation.isLoading,
      enableMemberStreakBadgeMutation.isLoading,
      disableMemberStreakBadgeMutation.isLoading,
      enableVerifiedBadgeMutation.isLoading,
      disableVerifiedBadgeMutation.isLoading,
      updateAccountSettingsBadgesMutation.isLoading,
    ],
  )

  return <BadgesContext.Provider value={value}>{children}</BadgesContext.Provider>
}
